/**
 * Event landing-page template — 08-product-launch ("Dark Reveal").
 *
 * Distinct CraftJS home-page document for the `productLaunch` theme. Cloned
 * from the proven `webinar` template; authored from the design spec in
 *   TEMPLATES - LANDINPAGES/08-product-launch/sections/*.html (CRAFT_PROPS_HINT).
 *
 * Visual style: dark-reveal — dark stage (#0a0a0a), iridescent/silver-violet
 * accent, bold modern sans, dramatic reveal, rounded-lg. The whole page sits
 * on a dark surface, so every TextChild carries a LIGHT color and the hero is
 * a full-bleed dark image with a dark overlay.
 *
 * Convention (mirrors webinar/temp.ts):
 *  - Section-level marketing copy → td() keys (unique `pl` prefix) → localized.
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also go
 *    through td() (Wave 3b): only fictitious proper names stay inline.
 *  - Global colors/fonts come from productLaunchPreset (applied via the theme);
 *    sections that own a surface set explicit per-node colors here.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// productLaunch-specific asset namespace (seeded under <CDN>/productLaunch/…)
const W = `${A}/shared/templates/08-product-launch`;

const ACCENT = "#c0c5cc"; // iridescent silver-violet accent
const STAGE = "#0a0a0a"; // dark stage background
const PANEL = "#141414"; // raised dark surface
const FOOTER_BG = "#070707"; // deepest footer surface
const LIGHT = "#ffffff"; // headings on dark
const ON_DARK = "rgba(255,255,255,0.72)"; // body text on dark
const MUTE = "#8b95a3"; // muted on dark
const SANS = "Inter, 'Helvetica Neue', Arial, sans-serif";
const DISPLAY = "'Neue Haas Grotesk', Inter, 'Helvetica Neue', sans-serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Child data (localized via td, Wave 3b) ────────────────────────────
// Only fictitious proper names stay inline; every user-visible content
// string of the structured rows resolves through websiteBuilderDefaults.

const SPEAKER_NAMES = [
  "Jonas Helmreich", "Mara Kjellberg", "Dr. Idris Okonkwo",
  "Léa Marchetti", "Karim Bensaïd", "Yara Costa",
];

const TESTIMONIAL_NAMES = ["Tomás Aravena", "Aurelia Wienand", "Henrik Lindqvist"];

// ── productLaunch-specific child factories ────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const n = i + 1;
  const d = { num: `<b>${td(`plNum${n}Value`)}</b>`, desc: td(`plNum${n}Desc`) };
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const n = i + 1;
  const d = {
    time: `<b>${td(`plAgendaRow${n}Time`)}</b>`,
    title: td(`plAgendaRow${n}Title`),
    speaker: td(`plAgendaRow${n}Who`),
    desc: td(`plAgendaRow${n}Desc`),
  };
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = { name: SPEAKER_NAMES[i], role: td(`plSpeaker${i + 1}Role`) };
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const n = i + 1;
  const d = {
    quote: td(`plTestimonial${n}Quote`),
    name: TESTIMONIAL_NAMES[i],
    role: td(`plTestimonial${n}Role`),
  };
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const n = i + 1;
  const d = { topic: `<b>${td(`plBlog${n}Topic`)}</b>`, content: td(`plBlog${n}Content`) };
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const n = i + 1;
  const d = { q: `<b>${td(`plFaq${n}Question`)}</b>`, a: td(`plFaq${n}Answer`) };
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/detail-edge.jpg`, `${W}/detail-iridescent.jpg`, `${W}/detail-monogram.jpg`,
  `${W}/hero-treatment.jpg`, `${W}/hero-einladung.jpg`, `${W}/hero-zusage.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const productLaunch = (td: TranslationFn): string => {
  const B = "pl_body";

  const HERO = "pl_hero";
  const NUMBERS = "pl_numbers";
  const AGENDA = "pl_agenda";
  const SPEAKER = "pl_speaker";
  const TEXT = "pl_text";
  const IMAGE = "pl_image";
  const COUNTDOWN = "pl_countdown";
  const GALLERY = "pl_gallery";
  const VIDEO = "pl_video";
  const TESTI = "pl_testimonials";
  const BLOG = "pl_blog";
  const FAQ = "pl_faq";
  const MAP = "pl_map";
  const DI = "pl_datainput";
  const CONTACT = "pl_contact";

  return enc({
    ...scaffold(
      { ab: "pl_ab", abs: "pl_abs", body: B, ft: "pl_ft", fts: "pl_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-black", padding: "p-4", borderRadius: "rounded-lg" },
    ),

    // ── Hero (full-bleed dark image, light text) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 100, contentAlignment: "center", imageLayout: "background", layoutWidth: "50%", inversed: false,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("plHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("plHeroHeader"), fontSizeHeader: "text-7xl", textAlignHeader: "center", headerColor: LIGHT,
        isVisibleText: true, textText: td("plHeroText"), fontSizeText: "text-lg", textAlignText: "center", textColor: ON_DARK,
        secondaryButtonSize: "medium", secondaryButtonText: td("plHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("plHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: false, backgroundOverlayColor: "rgba(10,10,10,0.72)",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: STAGE,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "pl_hero_eb", header: "pl_hero_hd", text: "pl_hero_tx", buttonOne: "pl_hero_b1", buttonTwo: "pl_hero_b2" },
    },
    pl_hero_eb: tc(HERO, { isVisible: true, text: td("plHeroEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_hero_hd: tc(HERO, { isVisible: true, text: td("plHeroHeader"), fontSize: "text-7xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_hero_tx: tc(HERO, { isVisible: true, text: td("plHeroText"), fontSize: "text-lg", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    pl_hero_b1: btn(HERO, { type: "secondary", text: td("plHeroSecondaryButton"), displayName: "buttonOne" }),
    pl_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("plHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers (spec strip) ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plNumbersEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["pl_num1", "pl_num2", "pl_num3", "pl_num4"],
      linkedNodes: { headline: "pl_num_eb", header: "pl_num_hd", text: "pl_num_tx" },
    },
    pl_num_eb: tc(NUMBERS, { isVisible: true, text: td("plNumbersEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_num_hd: tc(NUMBERS, { isVisible: true, text: td("plNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_num_tx: tc(NUMBERS, { isVisible: true, text: td("plNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    pl_num1: numbersChild(NUMBERS, 0, td),
    pl_num2: numbersChild(NUMBERS, 1, td),
    pl_num3: numbersChild(NUMBERS, 2, td),
    pl_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda (timeline) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plAgendaEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["pl_ag1", "pl_ag2", "pl_ag3", "pl_ag4", "pl_ag5"],
      linkedNodes: { headline: "pl_ag_eb", header: "pl_ag_hd", text: "pl_ag_tx" },
    },
    pl_ag_eb: tc(AGENDA, { isVisible: true, text: td("plAgendaEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_ag_hd: tc(AGENDA, { isVisible: true, text: td("plAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_ag_tx: tc(AGENDA, { isVisible: true, text: td("plAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    pl_ag1: agendaChild(AGENDA, 0, td),
    pl_ag2: agendaChild(AGENDA, 1, td),
    pl_ag3: agendaChild(AGENDA, 2, td),
    pl_ag4: agendaChild(AGENDA, 3, td),
    pl_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (hidden by default — no portraits) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plSpeakerEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["pl_sp1", "pl_sp2", "pl_sp3", "pl_sp4", "pl_sp5", "pl_sp6"],
      linkedNodes: { headline: "pl_sp_eb", header: "pl_sp_hd", text: "pl_sp_tx" },
    },
    pl_sp_eb: tc(SPEAKER, { isVisible: true, text: td("plSpeakerEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_sp_hd: tc(SPEAKER, { isVisible: true, text: td("plSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_sp_tx: tc(SPEAKER, { isVisible: true, text: td("plSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    pl_sp1: speakerChild(SPEAKER, 0, td),
    pl_sp2: speakerChild(SPEAKER, 1, td),
    pl_sp3: speakerChild(SPEAKER, 2, td),
    pl_sp4: speakerChild(SPEAKER, 3, td),
    pl_sp5: speakerChild(SPEAKER, 4, td),
    pl_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial — why 26 months) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: false, textHeadline: td("plTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("plTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("plTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "pl_tx_eb", header: "pl_tx_hd", text: "pl_tx_tx", buttonOne: "pl_tx_b1", buttonTwo: "pl_tx_b2" },
    },
    pl_tx_eb: tc(TEXT, { isVisible: false, text: td("plTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_tx_hd: tc(TEXT, { isVisible: true, text: td("plTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_tx_tx: tc(TEXT, { isVisible: true, text: td("plTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    pl_tx_b1: btn(TEXT, { type: "secondary", text: td("plTextSecondaryButton"), displayName: "buttonOne" }),
    pl_tx_b2: btn(TEXT, { type: "primary", text: td("plTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (detail — the edge, image left) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plImageEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("plImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("plImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("plImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-lg", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-edge.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "pl_im_eb", header: "pl_im_hd", text: "pl_im_tx", button: "pl_im_btn", image: "pl_im_img" },
    },
    pl_im_eb: tc(IMAGE, { isVisible: true, text: td("plImageEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    pl_im_hd: tc(IMAGE, { isVisible: true, text: td("plImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: LIGHT, header: true }),
    pl_im_tx: tc(IMAGE, { isVisible: true, text: td("plImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: ON_DARK, header: false }),
    pl_im_btn: btn(IMAGE, { text: td("plImageButton"), displayName: "button" }),
    pl_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-edge.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown (pre-reveal, dark) ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left", backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("plCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("plCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("plCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: DISPLAY, countdownFontColorDate: LIGHT,
        countdownSize: "text-base", countdownFontColor: MUTE, countdownBorderColor: "rgba(255,255,255,0.15)",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "pl_cd_eb", header: "pl_cd_hd", text: "pl_cd_tx", button: "pl_cd_btn" },
    },
    pl_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("plCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    pl_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("plCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: LIGHT, header: true }),
    pl_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("plCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    pl_cd_btn: btn(COUNTDOWN, { text: td("plCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (bento — hidden by default) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "3", layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plGalleryEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("plGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("plGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("plGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["pl_g1", "pl_g2", "pl_g3", "pl_g4", "pl_g5", "pl_g6"],
      linkedNodes: { headline: "pl_g_eb", header: "pl_g_hd", text: "pl_g_tx", secondary: "pl_g_b2", primary: "pl_g_b1" },
    },
    pl_g_eb: tc(GALLERY, { isVisible: true, text: td("plGalleryEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_g_hd: tc(GALLERY, { isVisible: true, text: td("plGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_g_tx: tc(GALLERY, { isVisible: false, text: td("plGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    pl_g_b2: btn(GALLERY, { type: "secondary", text: td("plGallerySecondaryButton"), displayName: "ButtonChild" }),
    pl_g_b1: btn(GALLERY, { text: td("plGalleryPrimaryButton"), displayName: "ButtonChild" }),
    pl_g1: galleryImage(GALLERY, 0),
    pl_g2: galleryImage(GALLERY, 1),
    pl_g3: galleryImage(GALLERY, 2),
    pl_g4: galleryImage(GALLERY, 3),
    pl_g5: galleryImage(GALLERY, 4),
    pl_g6: galleryImage(GALLERY, 5),

    // ── Video (concept film — hidden by default) ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false, backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plVideoEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("plVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "pl_vd_eb", header: "pl_vd_hd", text: "pl_vd_tx", button: "pl_vd_btn" },
    },
    pl_vd_eb: tc(VIDEO, { isVisible: true, text: td("plVideoEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_vd_hd: tc(VIDEO, { isVisible: true, text: td("plVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_vd_tx: tc(VIDEO, { isVisible: true, text: td("plVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    pl_vd_btn: btn(VIDEO, { text: td("plVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark panel surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: PANEL,
        isVisibleHeadline: true, textHeadline: td("plTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("plTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["pl_ts1", "pl_ts2", "pl_ts3"],
      linkedNodes: { headline: "pl_ts_eb", header: "pl_ts_hd", text: "pl_ts_tx" },
    },
    pl_ts_eb: tc(TESTI, { isVisible: true, text: td("plTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_ts_hd: tc(TESTI, { isVisible: true, text: td("plTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_ts_tx: tc(TESTI, { isVisible: false, text: td("plTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    pl_ts1: testimonialChild(TESTI, 0, td),
    pl_ts2: testimonialChild(TESTI, 1, td),
    pl_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (pre-reveal reading — hidden by default) ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: false, textHeadline: td("plBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["pl_bl1", "pl_bl2", "pl_bl3"],
      linkedNodes: { headline: "pl_bl_eb", header: "pl_bl_hd", text: "pl_bl_tx" },
    },
    pl_bl_eb: tc(BLOG, { isVisible: false, text: td("plBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_bl_hd: tc(BLOG, { isVisible: true, text: td("plBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_bl_tx: tc(BLOG, { isVisible: true, text: td("plBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    pl_bl1: blogChild(BLOG, 0, td),
    pl_bl2: blogChild(BLOG, 1, td),
    pl_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%", backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plLinkFaq"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("plFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["pl_fq1", "pl_fq2", "pl_fq3", "pl_fq4", "pl_fq5", "pl_fq6"],
      linkedNodes: { headline: "pl_fq_eb", header: "pl_fq_hd", text: "pl_fq_tx" },
    },
    pl_fq_eb: tc(FAQ, { isVisible: true, text: td("plLinkFaq"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    pl_fq_hd: tc(FAQ, { isVisible: true, text: td("plFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: LIGHT, header: true }),
    pl_fq_tx: tc(FAQ, { isVisible: true, text: td("plFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: MUTE, header: false }),
    pl_fq1: faqChild(FAQ, 0, td),
    pl_fq2: faqChild(FAQ, 1, td),
    pl_fq3: faqChild(FAQ, 2, td),
    pl_fq4: faqChild(FAQ, 3, td),
    pl_fq5: faqChild(FAQ, 4, td),
    pl_fq6: faqChild(FAQ, 5, td),

    // ── Map (industrial venue — hidden by default) ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false, backgroundColor: STAGE,
        isVisibleHeadline: true, textHeadline: td("plMapEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("plMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("plMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("plMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-lg",
        mapZoom: 13, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("plMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "pl_mp_eb", header: "pl_mp_hd", text: "pl_mp_tx", button: "pl_mp_btn" },
    },
    pl_mp_eb: tc(MAP, { isVisible: true, text: td("plMapEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    pl_mp_hd: tc(MAP, { isVisible: true, text: td("plMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: LIGHT, header: true }),
    pl_mp_tx: tc(MAP, { isVisible: true, text: td("plMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: ON_DARK, header: false }),
    pl_mp_btn: btn(MAP, { text: td("plMapButton"), displayName: "button" }),

    // ── DataInput (multi-step pre-order interest) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("plDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("plDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("plDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("plStepContact"), td("plStepInterest"), td("plStepPreorder"), td("plStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "pl_di_hl", header: "pl_di_hd", text: "pl_di_tx",
        fieldsGroup0: "pl_di_f0", fieldsGroup1: "pl_di_f1", fieldsGroup2: "pl_di_f2",
        fieldsGroup3: "pl_di_f3", fieldsGroup4: "pl_di_f4",
        companionsGroup: "pl_di_comp", overviewGroup: "pl_di_ov", checkboxes: "pl_di_cb",
        button: "pl_di_btn", backButton: "pl_di_back", nextButton: "pl_di_next",
      },
    },
    pl_di_hl: tc(DI, { isVisible: true, text: td("plDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    pl_di_hd: tc(DI, { isVisible: true, text: td("plDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    pl_di_tx: tc(DI, { isVisible: true, text: td("plDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    pl_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["pl_di_acceptance", "pl_di_first", "pl_di_last", "pl_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    pl_di_acceptance: acceptanceFi("pl_di_f0", td("acceptanceState")),
    pl_di_first: fi("pl_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    pl_di_last: fi("pl_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    pl_di_email: fi("pl_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    pl_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["pl_di_company", "pl_di_role"], linkedNodes: {},
    },
    pl_di_company: fi("pl_di_f1", { label: td("plFieldCompany"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "company", changeable: true }),
    pl_di_role: fi("pl_di_f1", { label: td("plFieldRole"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "role", changeable: true }),
    pl_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["pl_di_questions"], linkedNodes: {},
    },
    pl_di_questions: fi("pl_di_f2", { label: td("plFieldQuestions"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "questions", multiline: true, gridArea: "col-span-12", changeable: true }),
    pl_di_f3: emptyFieldsGroup(DI, 3),
    pl_di_f4: emptyFieldsGroup(DI, 4),
    pl_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["pl_di_comp_first"], linkedNodes: {},
    },
    pl_di_comp_first: fi("pl_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    pl_di_ov_t: tc("pl_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    pl_di_ov_x: tc("pl_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    pl_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["pl_di_ov_t", "pl_di_ov_x"], linkedNodes: {},
    },
    pl_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    pl_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("plDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    pl_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    pl_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: STAGE,
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("plContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("plContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("plContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "twitter", url: "https://twitter.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("plContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "pl_ct_eb", header: "pl_ct_hd", text: "pl_ct_tx",
        firstName: "pl_ct_first", lastName: "pl_ct_last", eMail: "pl_ct_email",
        phoneNumber: "pl_ct_phone", Message: "pl_ct_msg", button: "pl_ct_btn",
      },
    },
    pl_ct_eb: tc(CONTACT, { isVisible: true, text: td("plContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    pl_ct_hd: tc(CONTACT, { isVisible: true, text: td("plContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: LIGHT, header: true }),
    pl_ct_tx: tc(CONTACT, { isVisible: true, text: td("plContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: ON_DARK, header: false }),
    pl_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    pl_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    pl_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    pl_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    pl_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    pl_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("plContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Dark sticky bar, light nav, logo + nav + iridescent "Reserve a seat" CTA. */
export const productLaunchAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: STAGE, transparent: true,
      border: true, borderColor: "rgba(255,255,255,0.1)", borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("plLinkSpecs"), url: "#pl_numbers", linkType: "internal" },
        { label: td("plLinkRevealDay"), url: "#pl_agenda", linkType: "internal" },
        { label: td("plLinkSpeakers"), url: "#pl_speaker", linkType: "internal" },
        { label: td("plLinkFaq"), url: "#pl_faq", linkType: "internal" },
      ],
      linksColor: "#e2e8f0", linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("plHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: STAGE, logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Deep-dark footer, iridescent top rule, four link groups + social + copyright. */
export const productLaunchFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: FOOTER_BG,
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("plFooterProgram"), links: [
          { label: td("plLinkSpecs"), url: "#pl_numbers", linkType: "internal" },
          { label: td("plLinkRevealDay"), url: "#pl_agenda", linkType: "internal" },
          { label: td("plLinkSpeakers"), url: "#pl_speaker", linkType: "internal" },
        ] },
        { title: td("plFooterEvent"), links: [
          { label: td("plLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Berlin+Kreuzberg", linkType: "external" },
          { label: td("plLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Product+Reveal", linkType: "external" },
          { label: td("plLinkTickets"), url: "/register", linkType: "internal" },
        ] },
        { title: td("plFooterSupport"), links: [
          { label: td("plLinkContact"), url: "#pl_contact", linkType: "internal" },
          { label: td("plLinkFaq"), url: "#pl_faq", linkType: "internal" },
          { label: td("plLinkPress"), url: "#pl_contact", linkType: "internal" },
        ] },
        { title: td("plFooterLegal"), links: [
          { label: td("plLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("plLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("plLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: LIGHT, linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#94a3b8", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "twitter", platform: "twitter", url: "https://twitter.com" },
      ],
      socialColor: "#94a3b8", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#64748b", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B1) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero photo + dark scrim stay node props (backgroundOverlayColor); the
// css adds the silver-on-black poster treatment: one quiet spotlight cone,
// a silver rule that draws itself on entrance, uppercase letterspaced
// kickers, and the self-hosted faces so "Neue Haas Grotesk"/Inter resolve.
const CUSTOM_CSS = `/* theme:productLaunch */
${fontFaces([
  { family: "Neue Haas Grotesk", file: "archivo" },
  { family: "Inter", file: "inter-regular", weightRange: "400" },
  { family: "Inter", file: "inter-bold", weightRange: "700" },
])}

/* Stage hero — clip + isolate the spotlight layers over the photo scrim */
#pl_hero {
${heroShell}
}

/* Quiet spotlight: a single cold silver cone from above the stage
   (decorLayer's z-index 1 lifts it above the overlay div, still under the
   z-10 content) */
#pl_hero::before {
${decorLayer}
  inset: 0;
  background: radial-gradient(ellipse 62% 48% at 50% 0%, rgba(192, 197, 204, 0.16) 0%, rgba(192, 197, 204, 0) 72%);
}

/* Silver rule under the reveal block (draws itself on entrance) */
#pl_hero::after {
${decorLayer}
  left: 50%;
  bottom: 10%;
  width: 160px;
  height: 1px;
  margin-left: -80px;
  background: ${ACCENT};
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#pl_hero .text-xs, #pl_numbers .text-xs, #pl_agenda .text-xs, #pl_speaker .text-xs,
#pl_text .text-xs, #pl_image .text-xs, #pl_countdown .text-xs, #pl_gallery .text-xs,
#pl_video .text-xs, #pl_testimonials .text-xs, #pl_blog .text-xs, #pl_faq .text-xs,
#pl_map .text-xs, #pl_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("pl", 18)}
  @keyframes plRuleGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  /* Reveal entrance: the stage settles, then the silver rule draws itself */
  #pl_hero { animation: plFadeUp 0.8s ${EASE} both; }
  #pl_hero::after {
    transform-origin: center;
    animation: plRuleGrow 1.2s ${EASE} 0.5s both;
  }

${revealBlock("pl", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────

export const productLaunchPreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: ACCENT, secondary: MUTE, background: STAGE },
  fonts: { header: DISPLAY, body: SANS },
  rounding: "rounded-lg",
  recommendedSize: "large",
  bodyTheme: { fontColor: ON_DARK, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: DISPLAY, fontSize: "44px", fontColor: LIGHT },
    h2: { fontFamily: DISPLAY, fontSize: "26px", fontColor: LIGHT },
  },
  buttonsTheme: {
    primary: { bgColor: ACCENT, bgHoverColor: "#d6dbe1", borderColor: ACCENT, borderHoverColor: "#d6dbe1", fontColor: STAGE, fontHoverColor: STAGE, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
    secondary: { bgColor: "transparent", bgHoverColor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.35)", borderHoverColor: "#ffffff", fontColor: LIGHT, fontHoverColor: LIGHT, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
  },
  hyperlinkTheme: { fontColor: ACCENT, fontHoverColor: LIGHT, underline: false },
  formInputTheme: { labelFontColor: "#cbd5e1", inputFontColor: LIGHT, backgroundColor: PANEL, border: true, borderColor: "rgba(255,255,255,0.15)", borderRadius: "rounded-lg", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: SANS, fontSize: "text-sm", errorColor: "#DC2626" },
  customCss: CUSTOM_CSS,
};
